/**
 * Resource nodes — harvestable timber groves and stone outcrops scattered
 * across the map. Farms (buildings) are separate infinite food sources
 * worked directly. Nodes deplete; the workforce retargets automatically.
 */
export type NodeKind = "timber" | "stone";

export interface ResourceNode {
  id: number;
  kind: NodeKind;
  x: number;
  z: number;
  amount: number;
  maxAmount: number;
}

let nextNodeId = 1;

/** Keep node ids ahead of restored ids (save/load). */
export function reserveNodeId(id: number): void {
  nextNodeId = Math.max(nextNodeId, id + 1);
}

/** Deterministic RNG (mulberry32) — sim code must not use Math.random. */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

import type { Heightfield } from "./pcg";

export interface ScatterOpts {
  /** Seeded RNG. Defaults to the canonical skirmish seed. */
  rand?: () => number;
  /** Playable half-extent; nodes scatter inside ±0.925 × halfExtent. */
  halfExtent?: number;
  /** Keep nodes clear of the player base (default: origin). */
  playerBase?: { x: number; z: number };
  /** Keep nodes clear of the enemy base (default: the classic camp). */
  enemyBase?: { x: number; z: number };
  /** Clear radius around each base, world units (default 45). */
  clearRadius?: number;
  /** Node budget (defaults preserve the classic 12 timber / 8 stone). */
  timber?: number;
  stone?: number;
  /**
   * Shared terrain for placement: rejects nodes under open water or on
   * cliffs. Sim-side Heightfield, so the sim stays render-free.
   */
  terrain?: Heightfield | null;
}

/**
 * Deterministic-ish scatter avoiding the two bases. Called with no
 * arguments it reproduces the exact classic layout (half-extent 200,
 * bases at (0,0) and (-130,-70)); map sizes pass explicit opts.
 */
export function scatterNodes(
  randOrOpts: (() => number) | ScatterOpts = makeRng(20260921),
): ResourceNode[] {
  const o: ScatterOpts =
    typeof randOrOpts === "function" ? { rand: randOrOpts } : randOrOpts;
  const rand = o.rand ?? makeRng(20260921);
  const halfExtent = o.halfExtent ?? 200;
  const pb = o.playerBase ?? { x: 0, z: 0 };
  const eb = o.enemyBase ?? { x: -130, z: -70 };
  const clearRadius = o.clearRadius ?? 45;
  const timber = o.timber ?? 12;
  const stone = o.stone ?? 8;
  const nodes: ResourceNode[] = [];
  const clearOf = (x: number, z: number): boolean =>
    Math.hypot(x - pb.x, z - pb.z) > clearRadius &&
    Math.hypot(x - eb.x, z - eb.z) > clearRadius;
  const terrainOk = (x: number, z: number): boolean => {
    const t = o.terrain;
    if (!t) return true;
    // No nodes in rivers/lakes or on cliffs; workers and wagons can't
    // reach those, and the mesh would sit in the water.
    return !t.isWaterAt(x, z) && t.slopeAt(x, z) <= 0.55;
  };
  const place = (kind: NodeKind, count: number): void => {
    let guard = 0;
    while (count > 0 && guard++ < 500) {
      const x = (rand() * 2 - 1) * halfExtent * 0.925;
      const z = (rand() * 2 - 1) * halfExtent * 0.925;
      if (!clearOf(x, z) || !terrainOk(x, z)) continue;
      const amount = 400 + Math.floor(rand() * 300);
      nodes.push({ id: nextNodeId++, kind, x, z, amount, maxAmount: amount });
      count--;
    }
  };
  place("timber", timber);
  place("stone", stone);
  return nodes;
}
